import styled from 'styled-components';
import {peacock, peacockGradient} from './index.style';

export const MessageRow = styled.div<{isUser: boolean; isFullScreen?: boolean}>`
  display: flex;
  justify-content: ${({isUser}) => (isUser ? 'flex-end' : 'flex-start')};
  margin-top: clamp(0.625rem, 0.4vw + 0.5rem, 0.875rem);
  width: 100%;
`;

export const UserMessageWrap = styled.div<{isFullScreen?: boolean}>`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  max-width: ${({isFullScreen}) => (isFullScreen ? '736px' : '100%')};
  margin-left: auto;
  gap: clamp(0.375rem, 0.4vw, 0.625rem);
`;

export const UserBubble = styled.div`
  /* Don't sprawl on wide screens, don't crowd on narrow ones. */
  max-width: min(90%, 34rem);
  width: fit-content;
  padding: clamp(0.5rem, 0.4vw + 0.4rem, 0.75rem)
    clamp(0.75rem, 0.5vw + 0.6rem, 1rem);
  border-radius: clamp(0.375rem, 0.3vw + 0.3rem, 0.625rem);
  background: rgba(0, 160, 220, 0.08);
  border: 1px solid rgba(0, 90, 139, 0.18);
  color: ${peacock.deepest};
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const AiMessageWrap = styled.div<{isFullScreen?: boolean}>`
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: ${({isFullScreen}) => (isFullScreen ? '80%' : '100%')};
  gap: clamp(0.5rem, 0.5vw, 0.75rem);
  /* In the narrow side-panel, let the response + feedback card use the full
     available width. In fullscreen, cap the bubble at 75% of the available
     column so it doesn't run edge-to-edge on wide monitors. */
  padding-right: 0;
`;

export const AiIconWrap = styled.div`
  /* Fluid avatar: ~28px on phones → ~40px on desktop. */
  width: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  height: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  min-width: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  border-radius: 50%;
  background: ${peacockGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.3125rem, 0.3vw + 0.25rem, 0.5rem);

  .icon,
  svg,
  path {
    color: #ffffff !important;
    fill: #ffffff !important;
  }
`;

export const AiIconWrapTransparent = styled.div`
  width: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  height: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  min-width: clamp(1.75rem, 1.2vw + 1rem, 2.5rem);
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.3125rem, 0.3vw + 0.25rem, 0.5rem);
  color: #2d2d2d;

  .icon,
  svg,
  path {
    color: #2d2d2d !important;
    fill: #2d2d2d !important;
  }
`;

export const AiContent = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  padding-top: clamp(0.375rem, 0.3vw + 0.3rem, 0.625rem);
`;

export const AiText = styled.div`
  color: #2d2d2d;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  font-style: normal;
  font-weight: 400;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ChartCardWrap = styled.div`
  width: 100%;
  min-width: 0;
  margin-top: clamp(0.5rem, 0.5vw, 0.75rem);
  padding: clamp(0.625rem, 0.6vw + 0.4rem, 1rem);
  border-radius: 10px;
  background: #fafcfe;
  border: 1px solid rgba(0, 90, 139, 0.14);
`;

export const ChartCardTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  font-weight: 700;
  color: #2d2d2d;
`;

export const ChartCardDescription = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.6875rem, 0.3vw + 0.6rem, 0.8125rem);
  font-weight: 400;
  color: #6b7280;
  margin-top: 0.2rem;
`;

export const DataTableWrap = styled.div`
  width: 100%;
  min-width: 0;
  margin-top: clamp(0.5rem, 0.5vw, 0.75rem);
  padding: clamp(0.625rem, 0.6vw + 0.4rem, 1rem);
  border-radius: 10px;
  background: #fafcfe;
  border: 1px solid rgba(0, 90, 139, 0.14);
  overflow-x: auto;

  table {
    margin-top: 0.4rem !important;
  }
`;

export const DataTableLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.75rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${peacock.primary};
`;

export const CaveatText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.78125rem);
  font-style: italic;
  color: #8a8d93;
  margin: clamp(0.5rem, 0.5vw, 0.625rem) 0 0;
`;

export const ErrorText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  line-height: 1.55;
  color: #d40020;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

export const ChartHost = styled.div`
  width: 100%;
  min-width: 0;
  margin-top: clamp(0.375rem, 0.4vw, 0.625rem);
  /* dvh-relative so charts scale with viewport instead of being clipped
     by hardcoded 220/360 px caps on small/large screens. */
  min-height: clamp(12rem, 28dvh, 16rem);
  max-height: clamp(18rem, 42dvh, 24rem);
  /* Allow the chart toolbar (Chart Type / Color Palette dropdowns) to be
     scrolled horizontally if it ever exceeds the container width in the
     narrow side-panel mode. */
  overflow-x: auto;
  overflow-y: hidden;

  & > * {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0;
  }

  /* Toolbar rows inside the chart should wrap when space is tight rather
     than clipping their dropdowns. */
  [class*='toolbar'],
  [class*='controls'],
  [class*='Toolbar'],
  [class*='Controls'] {
    flex-wrap: wrap !important;
    gap: clamp(0.375rem, 0.5vw, 0.625rem) !important;
    min-width: 0 !important;
  }
`;
